import { SITE } from "@/lib/site";

// ---------------------------------------------------------------------------
// Shared content module for the 3 SEO landing pages + the Entity Advisor tool.
// Every string, FAQ, and structural attribute is centralised here so the
// pages stay lightweight and the JSON-LD schema is derived from the same
// source of truth as the on-page copy.
// ---------------------------------------------------------------------------

export const ENTITIES = {
  "pvt-ltd": {
    slug: "pvt-ltd",
    urlPath: "/pvt-ltd-registration-india",
    shortName: "Pvt Ltd",
    longName: "Private Limited Company",
    tagline: "The default choice for founders raising capital.",
    heroHeadline:
      "Private Limited Company Registration in India for First-Time Founders",
    "heroSubline":
      "Structure your startup the way investors expect. Founder-led incorporation for startups across Mumbai, Navi Mumbai, Thane and the MMRDA region with clarity on MOA, AOA, cap-table basics, and DPIIT-readiness.",
    keyword: "private limited registration india",
    priceHint: "Transparent, consultation-first pricing on WhatsApp",
    timeline: "7 to 12 working days",
    idealFor: [
      "Founders planning angel/VC/seed rounds",
      "Startups issuing ESOPs to early hires",
      "Businesses with 2+ co-founders",
      "SaaS, D2C, marketplaces, fintech",
    ],
    inclusions: [
      "Name reservation (RUN / SPICe+)",
      "DIN + DSC for 2 directors",
      "MOA & AOA drafting",
      "Certificate of Incorporation (COI)",
      "PAN, TAN, bank-account letter",
      "First board resolution + compliance kit",
    ],
    requiredDocs: [
      "PAN + Aadhaar of each director",
      "Passport-size photograph",
      "Address proof (bank statement or utility bill, ≤2 months old)",
      "Registered office proof (rent agreement + NOC or ownership proof)",
    ],
    postCompliance: [
      "INC-20A (Business commencement) within 180 days",
      "Statutory auditor appointment (Form ADT-1)",
      "First board meeting within 30 days",
      "Annual filings: AOC-4, MGT-7",
      "Income tax return & TDS filings",
    ],
    faqs: [
      {
        q: "Who should register a Private Limited Company in India?",
        a: "A Private Limited Company is the right structure for founders planning to raise capital, hire teams, or issue ESOPs. It gives you the credibility investors expect and a share-based structure that supports future funding rounds.",
        bullets: [
          "Best for VC / angel / seed-stage startups",
          "Supports up to 200 shareholders",
          "Cleanest structure for ESOP + cap-table",
          "Perceived as most credible by banks and enterprise clients",
        ],
      },
      {
        q: "How long does Private Limited Company registration take?",
        a: "Most Pvt Ltd incorporations complete in 7 to 12 working days end-to-end, subject to MCA processing and document readiness. We push updates daily on WhatsApp so you never chase for status.",
        bullets: [
          "Day 1 to 2: DSC + name reservation",
          "Day 3 to 5: MOA / AOA drafting + SPICe+ filing",
          "Day 6 to 12: MCA verification + COI + PAN + TAN",
        ],
      },
      {
        q: "What documents do I need to register a Pvt Ltd Company?",
        a: "Every director must submit KYC + address proof. You also need proof of your registered office address. Everything is signed digitally with your DSC.",
        bullets: [
          "PAN + Aadhaar of each director",
          "Passport-size photo",
          "Address proof (bank statement or utility bill, ≤ 2 months old)",
          "Registered office proof (rent agreement + NOC or ownership proof)",
        ],
      },
      {
        q: "What compliance is required after Pvt Ltd incorporation?",
        a: "Post-incorporation, a Pvt Ltd Company has a fixed annual compliance calendar. Missing it triggers penalties and can hurt future fundraising.",
        bullets: [
          "INC-20A within 180 days of incorporation",
          "First board meeting within 30 days",
          "Auditor appointment via Form ADT-1",
          "Annual ROC filings: AOC-4 + MGT-7",
          "Income tax return & TDS filings",
        ],
      },
      {
        q: "Can a Pvt Ltd Company be registered by a single person?",
        a: "No. A Private Limited Company requires a minimum of 2 directors and 2 shareholders. If you are a solo founder, an OPC (One Person Company) is the right starting point.",
        bullets: [
          "Minimum 2 directors, 2 shareholders",
          "Solo founders should register an OPC and convert later",
          "Free 15-minute call helps you decide",
        ],
      },
    ],
  },

  llp: {
    slug: "llp",
    urlPath: "/llp-registration-india",
    shortName: "LLP",
    longName: "Limited Liability Partnership",
    tagline: "Lean, partnership-friendly, low-compliance.",
    heroHeadline:
      "LLP Registration in India for Consulting, Agency and Professional Firms",
    heroSubline:
      "A Limited Liability Partnership is the right structure when you want partnership flexibility without a Pvt Ltd\u2019s compliance load. Ideal for firms not planning to raise VC capital.",
    keyword: "llp registration india",
    priceHint: "Transparent, consultation-first pricing on WhatsApp",
    timeline: "8 to 14 working days",
    idealFor: [
      "Consulting, agency and professional firms",
      "Bootstrapped businesses with 2+ partners",
      "Founders not planning VC fundraising",
      "Family-owned businesses seeking limited liability",
    ],
    inclusions: [
      "Name approval",
      "DPIN + DSC for 2 designated partners",
      "LLP Agreement drafting",
      "Certificate of Incorporation",
      "PAN + TAN",
    ],
    requiredDocs: [
      "PAN + Aadhaar of every partner",
      "Address proof of each partner",
      "Registered office proof (rent agreement + NOC or ownership proof)",
    ],
    postCompliance: [
      "LLP Agreement filing within 30 days of incorporation",
      "Annual filings: Form 11 (statement of accounts) + Form 8",
      "Income tax return filing",
      "GST compliance if registered",
    ],
    faqs: [
      {
        q: "Who should register an LLP in India?",
        a: "An LLP suits consulting, agency and professional firms with 2+ partners who want limited liability without the compliance burden of a Pvt Ltd. It is not suitable if you plan to raise VC capital.",
        bullets: [
          "Best for consulting, agency, and professional services",
          "2+ partners required",
          "Not suitable for VC fundraising",
          "Pass-through-style taxation",
        ],
      },
      {
        q: "How long does LLP registration take?",
        a: "Most LLPs are incorporated in 8 to 14 working days end-to-end. Delays usually happen only when partner KYC documents are inconsistent across databases.",
        bullets: [
          "Day 1 to 3: DSC + DPIN + name approval",
          "Day 4 to 8: LLP form filing (FiLLiP)",
          "Day 9 to 14: MCA verification + COI + PAN",
        ],
      },
      {
        q: "What documents are needed to register an LLP?",
        a: "You need partner KYC (PAN + Aadhaar), each partner\u2019s address proof, and proof of the registered office.",
        bullets: [
          "PAN + Aadhaar of every partner",
          "Address proof (bank statement or utility bill)",
          "Registered office rent agreement + NOC (or ownership proof)",
        ],
      },
      {
        q: "What compliance is required after LLP incorporation?",
        a: "LLPs have lighter compliance than Pvt Ltd but still have annual filings. Two forms + one income-tax return per year cover most of it.",
        bullets: [
          "LLP Agreement filing within 30 days",
          "Form 11 (annual return) by 30 May",
          "Form 8 (statement of accounts) by 30 October",
          "Income tax return by 30 September (audit) / 31 July (non-audit)",
        ],
      },
      {
        q: "Can an LLP be converted into a Pvt Ltd later?",
        a: "Yes. Many LLPs convert to Pvt Ltd when they decide to raise VC capital. We handle the conversion end-to-end.",
        bullets: [
          "Conversion is legally supported",
          "Cap-table + shareholding needs to be defined",
          "Book a 15-minute call before you convert",
        ],
      },
    ],
  },

  opc: {
    slug: "opc",
    urlPath: "/opc-registration-india",
    shortName: "OPC",
    longName: "One Person Company",
    tagline: "Solo founder? Get limited liability without a co-founder.",
    heroHeadline: "OPC Registration in India for Solo Founders",
    heroSubline:
      "A One Person Company gives solo founders a separate legal identity and limited liability. A strong first step before opening up to co-founders or raising capital.",
    keyword: "opc registration india",
    priceHint: "Transparent, consultation-first pricing on WhatsApp",
    timeline: "7 to 12 working days",
    idealFor: [
      "Solo founders testing a business idea",
      "Consultants wanting corporate identity",
      "Founders not yet ready to bring in a co-founder",
    ],
    inclusions: [
      "Name reservation",
      "DIN + DSC",
      "MOA + AOA",
      "Certificate of Incorporation",
      "Nominee declaration",
    ],
    requiredDocs: [
      "PAN + Aadhaar of the founder",
      "PAN + Aadhaar of the nominee",
      "Address proof",
      "Registered office proof",
    ],
    postCompliance: [
      "INC-20A within 180 days",
      "Annual filings: AOC-4 + MGT-7A",
      "Mandatory conversion to Pvt Ltd if turnover crosses \u20b92 Cr OR paid-up capital crosses \u20b950 L",
    ],
    faqs: [
      {
        q: "Who should register an OPC in India?",
        a: "An OPC is designed for solo founders who want corporate identity and limited liability without needing a co-founder. It is best for consultants and founders in early testing mode.",
        bullets: [
          "Only 1 director + 1 nominee",
          "Ideal for solo consultants and testing an idea",
          "Not suitable if you plan to raise VC in the next 12 months",
        ],
      },
      {
        q: "How long does OPC registration take?",
        a: "OPC incorporation typically completes in 7 to 12 working days end-to-end, similar to a Pvt Ltd.",
        bullets: [
          "Day 1 to 2: DSC + name reservation",
          "Day 3 to 6: MOA + AOA + SPICe+ filing",
          "Day 7 to 12: MCA verification + COI + PAN + TAN",
        ],
      },
      {
        q: "What documents are required to register an OPC?",
        a: "Founder KYC + nominee KYC + registered office proof.",
        bullets: [
          "PAN + Aadhaar of the founder",
          "PAN + Aadhaar of the nominee",
          "Address proof (bank / utility)",
          "Registered office proof",
        ],
      },
      {
        q: "When must an OPC convert to a Pvt Ltd?",
        a: "Conversion is mandatory when turnover exceeds \u20b92 Cr in any FY or paid-up capital exceeds \u20b950 L. Many OPCs also convert voluntarily when they bring in a co-founder.",
        bullets: [
          "Mandatory: turnover > \u20b92 Cr or paid-up > \u20b950 L",
          "Voluntary: when adding a co-founder or raising capital",
          "Conversion handled end-to-end by our team",
        ],
      },
      {
        q: "Can an OPC raise investment?",
        a: "OPCs cannot issue equity to investors. If your 12-month plan includes raising capital, register as a Pvt Ltd instead, or convert your OPC to Pvt Ltd before the round.",
        bullets: [
          "OPCs cannot issue equity",
          "Convert to Pvt Ltd before fundraising",
          "15-minute call helps you decide",
        ],
      },
    ],
  },
};

export const ENTITY_LIST = Object.values(ENTITIES);

// ------- Structured-data (JSON-LD) generators -------

export const professionalServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "HG Corporates",
  alternateName: "HG Corporates",
  url: "https://hgcorporates.com/",
  logo: "https://hgcorporates.com/brand/logo.png",
  image: "https://hgcorporates.com/og.jpg",
  email: SITE.email,
  telephone: "+91-9797193555",
  founder: { "@type": "Person", name: SITE.founder },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.708, 7th Floor, Nirmal Ecstasy Business Park, Near East-West Flyover",
    addressLocality: "Mulund (West), Mumbai",
    addressRegion: "Maharashtra",
    postalCode: "400080",
    addressCountry: "IN",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: String(SITE.googleRating),
    reviewCount: String(SITE.googleReviewCount),
    bestRating: "5",
    worstRating: "1",
  },
  areaServed: "IN",
  priceRange: "\u20b9\u20b9",
});

export const faqPageSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: `${f.a} ${(f.bullets || []).join(". ")}`,
    },
  })),
});

// ------- Advisor recommendation engine -------

// Inputs:
//   fundraising: "yes" | "no"
//   founders:    "1"   | "2+"
//   priority:    "low-compliance" | "investor-ready" | "solo-ownership"

export function recommendEntity({ fundraising, founders, priority }) {
  if (fundraising === "yes") {
    if (founders === "1") {
      return {
        primary: ENTITIES["pvt-ltd"],
        fallback: ENTITIES.opc,
        reason:
          "You're planning to raise capital and you're currently a solo founder. Investors require a share-based structure with room for multiple shareholders \u2014 Pvt Ltd is the right starting point. If you'd rather begin solo, an OPC can be converted to Pvt Ltd before the round.",
      };
    }
    return {
      primary: ENTITIES["pvt-ltd"],
      reason:
        "You're planning to raise capital with 2+ founders. Pvt Ltd is the default investor-friendly structure \u2014 clean cap-table, ESOP-ready, and highest credibility with VCs.",
    };
  }

  // fundraising === "no"
  if (founders === "1") {
    if (priority === "investor-ready") {
      return {
        primary: ENTITIES["pvt-ltd"],
        reason:
          "You're solo and not raising immediately, but investor-readiness is your priority. Register as Pvt Ltd from day one so you never have to convert later. You can hold 100% shareholding as sole founder with a co-director on record.",
      };
    }
    return {
      primary: ENTITIES.opc,
      fallback: ENTITIES["pvt-ltd"],
      reason:
        "You're a solo founder, not raising capital, and prioritising solo ownership. OPC gives you limited liability and corporate identity without needing a co-founder. Convert to Pvt Ltd later if fundraising becomes relevant.",
    };
  }

  // founders === "2+" AND not fundraising
  if (priority === "low-compliance") {
    return {
      primary: ENTITIES.llp,
      reason:
        "You have 2+ partners, are not raising VC capital, and want the lightest compliance load. LLP is ideal \u2014 partnership flexibility with limited liability, and only 2 annual forms instead of a Pvt Ltd's full compliance calendar.",
    };
  }
  if (priority === "investor-ready") {
    return {
      primary: ENTITIES["pvt-ltd"],
      fallback: ENTITIES.llp,
      reason:
        "You have 2+ partners and want to be investor-ready even if you're not raising immediately. Pvt Ltd future-proofs the structure so you never have to convert during a live round.",
    };
  }
  // solo-ownership picked with 2+ founders \u2014 mismatched; recommend LLP with note
  return {
    primary: ENTITIES.llp,
    reason:
      "With 2+ founders, solo ownership doesn't apply. Based on your remaining answers, LLP is the closest fit \u2014 lean, partnership-first, low-compliance.",
  };
}
